import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import _isEqual from 'lodash/isEqual';
import { useLocalStorage } from 'usehooks-ts';
import { Value } from '@lib/interfaces/form';
import TabBar from '@lib/components/TabBar/TabBar';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import { Modals } from '@lib/enums/modals';
import useModalManagement from '@lib/hooks/useModalManagement';
import usePrevious from '@lib/hooks/usePrevious';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Trans from '@lib/components/Trans/Trans';
import { DASHBOARD_MONITORING_FILTER_FORM } from '@lib/enums/localStorageKeys';
import MonitoringFilterFieldNames from '@lib/enums/fieldNames/monitoringFilterFieldNames';
import { DASHBOARD_MONITORING_FILTER_FORM_EMPTY_VALUES } from './constants';
import MonitoringFilterByEntityId from './MonitoringFilter/MonitoringFilterByEntityId';
import MonitoringRangesGraph from './MonitoringRangesGraph/MonitoringRangesGraph';
import MonitoringPerformance from './MonitoringPerformance/MonitoringPerformance';
import MonitoringFilter from './MonitoringFilter/MonitoringFilter';
import MonitoringMap from './MonitoringMap/MonitoringMap';
import getAdditionalTaskFilters from './utils/getAdditionalTaskFilters';
import getMonitoringEntity from './utils/getMonitoringEntity';
import getPageTabs from './utils/getPageTabs';
import useActions from './utils/useActions';
import useQueries from './utils/useQueries';
import { MonitoringEntity } from './enums';
import styles from './Monitoring.module.scss';
import getFilterFieldNameByMonitoringEntity from './utils/getFilterFieldNameByMonitoringEntity';

// Define which fields should affect the MonitoringPerformance key
const monitoringPerformanceFiltersDependenciesArray = [
  MonitoringFilterFieldNames.FormCategory,
  MonitoringFilterFieldNames.FormTemplateName,
  MonitoringFilterFieldNames.Period,
  MonitoringFilterFieldNames.FilterPresetId,
];

const filterModal = Modals.DashboardMonitoringFilterModal;
const modals = [Modals.DashboardMonitoringFilterModal];

const pageTabs = getPageTabs();

export default function Monitoring() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(
    DASHBOARD_MONITORING_FILTER_FORM,
    DASHBOARD_MONITORING_FILTER_FORM_EMPTY_VALUES,
  );
  const prevFilterParams = usePrevious(filterParams);
  const monitoringEntity = getMonitoringEntity({ filterParams });

  const taskFilters = useMemo(
    () => getAdditionalTaskFilters({ filterParams, monitoringEntity }),
    [filterParams, monitoringEntity],
  );

  const useModalManagementResult = useModalManagement(modals);

  const useQueriesResult = useQueries({ monitoringEntity });

  const useActionsResult = useActions({
    useQueriesResult,
    filterParams,
    setFilterParams,
  });
  const {
    onUpdateFilterParams,
    isReady,
    isApplyPresetLoading,
    isLoading,
    resetFilterParams,
  } = useActionsResult;

  const onTabChange = (item?: TabBarItemType) =>
    item?.value
      ? onUpdateFilterParams({
          [MonitoringFilterFieldNames.Entity]: item.value,
        })
      : undefined;

  const buildPerformanceDeps = useCallback(
    (source: Record<string, unknown> | undefined, entity: MonitoringEntity) => {
      if (!source) return undefined;
      const deps: Record<string, unknown> = {};
      monitoringPerformanceFiltersDependenciesArray.forEach((name) => {
        const key = getFilterFieldNameByMonitoringEntity(entity, name);
        deps[name] = source[key];
      });
      return deps;
    },
    [],
  );

  const currentPerformanceDeps = useMemo(
    () => buildPerformanceDeps(filterParams, monitoringEntity),
    [filterParams, monitoringEntity, buildPerformanceDeps],
  );
  const prevPerformanceDeps = useMemo(
    () => buildPerformanceDeps(prevFilterParams, monitoringEntity),
    [prevFilterParams, monitoringEntity, buildPerformanceDeps],
  );

  const [monitoringPerformanceKeyIndex, setMonitoringPerformanceKeyIndex] =
    useState(0);
  useEffect(() => {
    if (!_isEqual(prevPerformanceDeps, currentPerformanceDeps)) {
      setMonitoringPerformanceKeyIndex((i) => i + 1);
    }
  }, [prevPerformanceDeps, currentPerformanceDeps]);

  const monitoringPerformanceKey = `${monitoringEntity}-${monitoringPerformanceKeyIndex}`;

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageHeader title={t('monitoring')} />
      {isLoading && !isReady && <CircleLoader />}
      {!isReady && !isLoading && (
        <PageBody>
          <ListEmptyState
            title={t('no-monitoring-data-yet')}
            text={<Trans i18nKey="no-monitoring-data-text" />}
            actions={[]}
          />
        </PageBody>
      )}
      {isReady && !isLoading && (
        <div className={styles.root}>
          <div className={styles.sticky}>
            <TabBar
              items={pageTabs}
              active={monitoringEntity}
              onActiveChange={onTabChange}
              useSidePaddings
            />
            <MonitoringFilter
              filterParams={filterParams}
              modal={filterModal}
              useActionsResult={useActionsResult}
              useModalManagementResult={useModalManagementResult}
              useQueriesResult={useQueriesResult}
            />
          </div>
          <PageBody>
            <MonitoringRangesGraph
              filterParams={filterParams}
              taskFilters={taskFilters}
              key={monitoringEntity}
              onResetFilters={resetFilterParams}
            >
              {monitoringEntity === MonitoringEntity.Site && (
                <MonitoringFilterByEntityId
                  useQueriesResult={useQueriesResult}
                  filterParams={filterParams}
                  onUpdateFilterParams={onUpdateFilterParams}
                  name={MonitoringFilterFieldNames.Sites}
                  isLoading={isApplyPresetLoading}
                />
              )}
              {monitoringEntity === MonitoringEntity.Sector && (
                <MonitoringFilterByEntityId
                  useQueriesResult={useQueriesResult}
                  filterParams={filterParams}
                  onUpdateFilterParams={onUpdateFilterParams}
                  name={MonitoringFilterFieldNames.Sectors}
                  isLoading={isApplyPresetLoading}
                />
              )}
            </MonitoringRangesGraph>
            <div className={styles.componentsRow}>
              <div>
                <MonitoringPerformance
                  filterParams={filterParams}
                  taskFilters={taskFilters}
                  key={monitoringPerformanceKey}
                />
              </div>
              <div>
                <MonitoringMap
                  key={monitoringEntity}
                  taskFilters={taskFilters}
                  filterParams={filterParams}
                  onUpdateFilterParams={onUpdateFilterParams}
                />
              </div>
            </div>
          </PageBody>
        </div>
      )}
    </>
  );
}
